import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import { Maybe } from '@Types'
import { observer } from 'mobx-react'
import PaginationComponent from '@Components/Tables/Pagination'
import CatalogMobileSubLinks from '@Components/Navigations/CatalogMobileSubLinks'
import { getNumTitles } from '@App/helpers/pagination/get-num-titles'
import PaginationResultsBlock from '@Components/Navigations/PaginationResultsBlock'
import { StoresContext } from '@App/contexts'
import { PaginationBody } from '@Entities/pagination'
import ContentHeader from '@Components/ContentHeader'
import DeleteDraftModal from '@Components/Navigations/CreateWorkBottomNavs/DeleteDraftModal'
import RegistrationDraftsContentHeader from '@Scenes/RightsholderScene/RightsholderCatalogRegistrationDrafts/RegistrationDraftsContentHeader'
import RegistrationDraftsTable from '@Scenes/RightsholderScene/RightsholderCatalogRegistrationDrafts/RegistrationDraftsTable'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'

const limitItems = 10

const RightsholderCatalogRegistrationDrafts = () => {
    const { workStore, rightsholderStore, workSearchStore, uiStore, userStore } = useContext(StoresContext)

    const [deleteAllPopupOpen, setDeleteAllPopupOpen] = useState<boolean>(false)
    const [deleteIndividualPopupOpen, setDeleteIndividualPopupOpen] = useState<boolean>(false)
    const [deletedTitle, setDeletedTitle] = useState<string | undefined>()
    const [deletedId, setDeletedId] = useState<Maybe<number>>()
    const [loading, setLoading] = useState<boolean>(false)

    const openDeleteModal = (typeAll: boolean, title?: string, id?: number) => {
        if (typeAll) {
            setDeleteAllPopupOpen(true)
        } else {
            setDeletedTitle(title)
            setDeletedId(id)
            setDeleteIndividualPopupOpen(true)
        }
    }

    const closeDeleteModal = () => {
        if (deleteAllPopupOpen) {
            setDeleteAllPopupOpen(false)
        } else {
            setDeleteIndividualPopupOpen(false)
        }
    }

    const eventGetWorksDrafts = async (
        pagination: PaginationBody = {
            page: 1,
            limit: limitItems,
        }
    ) => {
        setLoading(true)
        await workSearchStore.getWorksDraftsRightsHolder(rightsholderStore.currentRightsholderId, pagination)
        setLoading(false)
    }

    const handleDeleteAllDrafts = async () => {
        await workStore.deleteAllDraftsRightsHolder(rightsholderStore.currentRightsholderId)
        closeDeleteModal()
        await eventGetWorksDrafts()
    }

    const handleDeleteDraft = async (deleteThisId: Maybe<number>) => {
        if (!deleteThisId) {
            return
        }
        await workStore.deleteDraftRightsHolder(deleteThisId)
        closeDeleteModal()
        await eventGetWorksDrafts()
    }

    useEffect(() => {
        analyticsSendPage('rightsholder_registration_drafts')
        eventGetWorksDrafts().then()
        return () => {
            workSearchStore.destroyWorksDraftsRightsHolderPaginationInfo()
        }
    }, [])

    const isMobile = uiStore.isMobile
    const workDrafts = workSearchStore.worksDraftsRightsHolder
    const paginationPages = workSearchStore.worksDraftsRightsHolderPaginationInfo
    const numTitles =
        workDrafts && paginationPages && getNumTitles(workDrafts.count, paginationPages.currentPage, limitItems)

    const isInternal = userStore.isInternal

    return (
        <>
            <title>Registration Drafts</title>
            <div className="rightsholder-catalog-drafts-wrapper">
                <ContentHeader title="Registration Drafts" descriptionElement={<RegistrationDraftsContentHeader />} />
                <div className="info-text">
                    <p className="bold">
                        You have{' '}
                        {!workDrafts || workDrafts.count === 0
                            ? 'no works'
                            : workDrafts.count === 1
                              ? '1 work'
                              : `${workDrafts.count} works`}{' '}
                        saved for later
                    </p>
                </div>
                {isMobile && <CatalogMobileSubLinks />}
                <section className="showing-search">
                    <PaginationResultsBlock
                        totalResults={workDrafts?.count ? workDrafts.count : 0}
                        workNumTitles={numTitles}
                    />
                    <a className="link bold delete-all" onClick={() => openDeleteModal(true)}>
                        Delete All
                    </a>
                </section>
                {loading ? <PointPreloader /> : <RegistrationDraftsTable openDeleteModal={openDeleteModal} />}
                {workDrafts && workDrafts.count > limitItems && (
                    <PaginationComponent
                        limit={limitItems}
                        paginationPages={paginationPages}
                        onChange={eventGetWorksDrafts}
                    />
                )}
            </div>
            <DeleteDraftModal
                open={deleteAllPopupOpen || deleteIndividualPopupOpen}
                closeModal={closeDeleteModal}
                title={deletedTitle}
                individual={!deleteAllPopupOpen}
                handleDeleteDraft={() => (deleteAllPopupOpen ? handleDeleteAllDrafts() : handleDeleteDraft(deletedId))}
                disabled={isInternal}
            />
        </>
    )
}

export default observer(RightsholderCatalogRegistrationDrafts)
